<template>
  <div
    v-if="src"
    class="app-svg-icon app-svg-icon--asset"
    :style="iconStyle"
    :aria-label="title || undefined"
    :aria-hidden="title ? undefined : 'true'"
    role="img"
    v-html="assetMarkup"
  />
  <svg
    v-else
    class="app-svg-icon"
    :style="iconStyle"
    :aria-hidden="title ? undefined : 'true'"
    :aria-label="title || undefined"
    role="img"
  >
    <title v-if="title">{{ title }}</title>
    <use :href="symbolHref" />
  </svg>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from "vue";

const props = defineProps({
  name: {
    type: String,
    default: "",
  },
  src: {
    type: String,
    default: "",
  },
  size: {
    type: [String, Number],
    default: "1em",
  },
  color: {
    type: String,
    default: "",
  },
  fill: {
    type: String,
    default: "",
  },
  title: {
    type: String,
    default: "",
  },
});

const PX_TO_REM_ROOT = 37.5;
const DEFAULT_ICON_COLORS = Object.freeze({
  icon: "#999",
});

const normalizeSize = (value) => {
  const normalized = String(value).trim();
  if (!/^-?\d+(?:\.\d+)?$/.test(normalized)) {
    return normalized;
  }

  return `${Number(normalized) / PX_TO_REM_ROOT}rem`;
};

const symbolHref = computed(() => `#wp-${props.name}`);
const effectiveColor = computed(
  () =>
    props.fill || props.color || DEFAULT_ICON_COLORS[props.name] || undefined,
);
const iconStyle = computed(() => ({
  "--app-svg-icon-size": normalizeSize(props.size),
  color: effectiveColor.value,
  fill: props.fill || undefined,
}));

const assetMarkup = ref("");
let assetLoadVersion = 0;

async function loadAssetMarkup(src) {
  const version = ++assetLoadVersion;
  assetMarkup.value = "";
  if (!src) {
    return;
  }

  try {
    const response = await fetch(src);
    if (!response.ok) {
      return;
    }
    const markup = await response.text();
    if (version === assetLoadVersion) {
      assetMarkup.value = markup;
    }
  } catch (_) {
    // Keep the component empty when an external asset cannot be loaded.
  }
}

watch(() => props.src, loadAssetMarkup, { immediate: true });

onBeforeUnmount(() => {
  assetLoadVersion += 1;
});
</script>

<style scoped>
.app-svg-icon {
  display: inline-block;
  width: var(--app-svg-icon-size);
  height: var(--app-svg-icon-size);
  flex: none;
  overflow: hidden;
  vertical-align: -0.125em;
  fill: currentColor;
}

.app-svg-icon--asset {
  line-height: 0;
}

.app-svg-icon--asset :deep(svg) {
  display: block;
  width: 100%;
  height: 100%;
}
</style>
